import axios from 'axios';
import { API_BASE_URL } from '@/lib/config';
import { clearAccessToken, getAccessToken, isViewAsTab } from '@/lib/auth/session';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { ROUTES } from '@/lib/constants/routes';
import { getApiErrorCode } from '@/lib/utils/error.utils';

/**
 * Shared HTTP client. All API modules use this instance (never call axios/fetch
 * from components). Attaches the session bearer token and clears the session on
 * a 401 so the app falls back to the login screen. In a view-as tab whose
 * session has ended (MYN-335), it drops that tab's token and shows the
 * "session ended" page instead, since no screen can load any more.
 */
export const apiClient = axios.create({ baseURL: API_BASE_URL });

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const status =
      typeof error === 'object' && error !== null && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;
    // A view-as token outlives its session by nothing: once the 30 minutes are
    // up it is simply expired, and the Backend answers 401 before it can say
    // VIEW_AS_SESSION_ENDED. Either way the tab is done, never a login page.
    const viewAsEnded =
      isViewAsTab() &&
      (status === 401 ||
        (status === 403 && getApiErrorCode(error) === API_ERROR_CODES.VIEW_AS_SESSION_ENDED));
    if (viewAsEnded) {
      clearAccessToken();
      // The ended page itself may still fire a tokenless request: no reload loop.
      if (window.location.pathname !== ROUTES.VIEW_AS) window.location.assign(ROUTES.VIEW_AS);
    } else if (status === 401) {
      clearAccessToken();
    }
    return Promise.reject(error);
  },
);
